/**
 * /admin/finances — global revenue / cost / margin dashboard.
 *
 * Backend already serves the data via two endpoints (owner_api.py):
 *   GET /api/admin/v2/finances/summary/?range=mtd|30d|90d|ytd
 *   GET /api/admin/v2/finances/users/?range=...&order=-revenue|margin
 *
 * The per-user drawer + full ledger lives on /admin/users. This page is
 * the aggregate roll-up + a leaderboard of high-revenue / high-loss
 * users so the founder can decide who's worth a cost-down conversation.
 *
 * USD -> INR conversion is fixed at 83 per AIUsage page convention. The
 * accountant uses Cost Explorer for the real number; this surface is
 * for operational triage, not for filing.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw, TrendingUp, TrendingDown, Users, Wallet, Activity,
  AlertTriangle, Loader2,
} from "lucide-react";
import {
  getFinancesSummary,
  listUserFinancials,
} from "../../api/owner.api";

const INR_PER_USD = 83;

const RANGES = [
  { key: "mtd",  label: "Month-to-date" },
  { key: "30d",  label: "Last 30 days" },
  { key: "90d",  label: "Last 90 days" },
  { key: "ytd",  label: "Year-to-date" },
];

function fmtInr(amount) {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  }).format(amount);
}
function fmtUsd(amount) {
  if (amount == null) return "—";
  return `$${Number(amount).toFixed(2)}`;
}
function fmtPct(num) {
  if (num == null || !isFinite(num)) return "—";
  return `${num.toFixed(1)}%`;
}

export default function AdminFinances() {
  const [range, setRange] = useState("mtd");
  const [summary, setSummary] = useState(null);
  const [topRevenue, setTopRevenue] = useState([]);
  const [topLosses, setTopLosses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      // Three calls in parallel — summary for the KPI strip, two
      // sorted user pages for the leaderboards. Backend pagination
      // returns 25 per page; we slice to the top 10 for display.
      const [s, r, l] = await Promise.all([
        getFinancesSummary(range),
        listUserFinancials({ range, order: "-revenue", segment: "all" }),
        listUserFinancials({ range, order: "margin",  segment: "all" }),
      ]);
      setSummary(s.data);
      setTopRevenue((r.data.results || []).slice(0, 10));
      setTopLosses((l.data.results  || []).slice(0, 10));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  const grossMarginInr = useMemo(() => {
    if (!summary) return null;
    return summary.revenue.total_inr - summary.cost.total_usd * INR_PER_USD;
  }, [summary]);

  const grossMarginPct = useMemo(() => {
    if (!summary || !summary.revenue.total_inr) return null;
    return (grossMarginInr / summary.revenue.total_inr) * 100;
  }, [summary, grossMarginInr]);

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Finances</h1>
          <p className="text-sm text-text-secondary mt-1">
            Revenue vs cost rollup. AI cost is real (per-call USD logged on
            every redemption); storage + infra share = 0 until Phase 5
            wires AWS Cost Explorer.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border-default text-sm hover:bg-bg-secondary"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </header>

      {/* Range pills */}
      <div className="flex flex-wrap gap-2">
        {RANGES.map((r) => {
          const active = range === r.key;
          return (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                active
                  ? "bg-button-primary text-white border-button-primary"
                  : "bg-bg-primary border-border-default text-text-secondary hover:text-text-primary hover:border-text-secondary"
              }`}
            >
              {r.label}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 p-3 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {loading && !summary && (
        <div className="rounded-lg border border-border-default bg-bg-primary p-12 text-center text-text-secondary text-sm">
          <Loader2 className="w-5 h-5 inline-block mr-2 animate-spin" />
          Crunching the ledger…
        </div>
      )}

      {summary && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              icon={<TrendingUp className="w-4 h-4 text-emerald-300" />}
              label="Revenue"
              value={fmtInr(summary.revenue.total_inr)}
              sub={`Top-ups ${fmtInr(summary.revenue.topups_inr)} · Subs ${fmtInr(summary.revenue.subscriptions_inr || 0)} · Packs ${fmtInr(summary.revenue.pack_sales_inr)}`}
            />
            <KpiCard
              icon={<TrendingDown className="w-4 h-4 text-rose-300" />}
              label="Variable cost"
              value={fmtUsd(summary.cost.total_usd)}
              sub={`AI ${fmtUsd(summary.cost.ai_usd)} · Storage ${fmtUsd(summary.cost.storage_usd)} · Infra ${fmtUsd(summary.cost.infra_share_usd)}`}
            />
            <KpiCard
              icon={<Wallet className="w-4 h-4 text-amber-300" />}
              label="Gross margin"
              value={grossMarginInr == null ? "—" : fmtInr(grossMarginInr)}
              sub={
                grossMarginPct == null
                  ? "no revenue yet"
                  : (grossMarginPct >= 90
                      ? <span className="text-emerald-300">{fmtPct(grossMarginPct)} — healthy</span>
                      : grossMarginPct >= 75
                        ? <span className="text-amber-300">{fmtPct(grossMarginPct)} — review</span>
                        : <span className="text-rose-300">{fmtPct(grossMarginPct)} — pricing leak</span>)
              }
            />
            <KpiCard
              icon={<Users className="w-4 h-4 text-blue-300" />}
              label="Users"
              value={summary.users.total.toLocaleString("en-IN")}
              sub={`${summary.users.active_subs} active subs · ${summary.users.new_in_range} new in range`}
            />
          </div>

          <div className="text-[11px] text-text-secondary flex items-center gap-2">
            <Activity className="w-3 h-3" />
            As of {new Date(summary.as_of).toLocaleString()} · since {new Date(summary.since).toLocaleString()} · USD→INR fixed at {INR_PER_USD}.
          </div>

          {/* Leaderboards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <UserLeaderboard
              title="Top revenue users"
              hint="Sorted by total revenue this range — biggest spenders first."
              rows={topRevenue}
              columnLabel="Revenue"
              valueOf={(u) => fmtInr(u.revenue?.total_inr)}
              marginCol
            />
            <UserLeaderboard
              title="Highest losses"
              hint="Sorted by margin ascending — users where cost > revenue surface first. Consider plan migration or cost-down conversation."
              rows={topLosses}
              columnLabel="Margin"
              valueOf={(u) => fmtInr(u.margin_inr)}
              marginCol
              danger
            />
          </div>
        </>
      )}
    </div>
  );
}


function KpiCard({ icon, label, value, sub }) {
  return (
    <div className="rounded-lg border border-border-default bg-bg-primary p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-text-secondary">
        {icon}
        {label}
      </div>
      <div className="font-mono text-xl mt-1 truncate">{value}</div>
      <div className="text-[11px] text-text-secondary mt-1 truncate">{sub}</div>
    </div>
  );
}


function UserLeaderboard({ title, hint, rows, columnLabel, valueOf, marginCol, danger }) {
  return (
    <div className="rounded-lg border border-border-default bg-bg-primary overflow-hidden">
      <div className="px-4 py-3 border-b border-border-default">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          {danger
            ? <TrendingDown className="w-4 h-4 text-rose-300" />
            : <TrendingUp className="w-4 h-4 text-emerald-300" />}
          {title}
        </h3>
        <p className="text-[11px] text-text-secondary mt-0.5">{hint}</p>
      </div>
      <table className="w-full text-sm">
        <caption className="sr-only">{title}{hint ? ` — ${hint}` : ""}</caption>
        <thead className="bg-table-header text-left text-[11px] uppercase tracking-widest text-text-secondary">
          <tr>
            <th scope="col" className="px-3 py-2">User</th>
            <th scope="col" className="px-3 py-2 hidden sm:table-cell">Plan</th>
            <th scope="col" className="px-3 py-2 text-right">{columnLabel}</th>
            {marginCol && (
              <th scope="col" className="px-3 py-2 text-right hidden md:table-cell">Cost (USD)</th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={4} className="px-3 py-8 text-center text-text-secondary text-xs">No users in this range yet.</td></tr>
          )}
          {rows.map((u) => (
            <tr key={u.user_id} className="border-t border-border-default">
              <td className="px-3 py-2">
                <Link
                  to={`/admin/users?segment=all&search=${encodeURIComponent(u.email)}`}
                  className="text-button-primary hover:underline font-mono text-xs"
                >
                  {u.email}
                </Link>
                <div className="text-[10px] text-text-secondary truncate">{u.full_name}</div>
              </td>
              <td className="px-3 py-2 text-xs text-text-secondary hidden sm:table-cell">
                {u.plan?.plan_code || "—"}
              </td>
              <td className={`px-3 py-2 text-right font-mono tabular-nums ${
                danger && u.margin_inr < 0 ? "text-rose-300" : ""
              }`}>
                {valueOf(u)}
              </td>
              {marginCol && (
                <td className="px-3 py-2 text-right font-mono tabular-nums text-text-secondary hidden md:table-cell">
                  {fmtUsd(u.cost?.total_usd || 0)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
